import { 
  Input,
  Output,
  Directive,
  ElementRef,
  HostBinding,
  EventEmitter,
  HostListener,
}
from '@angular/core';

/**
 * enums
*/
import { InputType } from '@sharedModules/forms/enums/input-type';
import { FormFieldUI } from '@shared/modules/forms/enums/form-field-ui';
import { IFormFieldSizeType } from '@sharedModules/forms/enums/form-field-size';

/**
 * interfaces
 */
import { IFormInputFieldUI } from './interfaces/form-input-field';

/**
 * utils
 */
import { FromFieldUtils } from '@sharedModules/forms/utils/form-field-utils';

/**
 * @directive FormInputFieldDirective
 * @description
 * **Angular directive for enhanced form input field styling** and behavior management.
 *
 * This directive provides comprehensive styling and behavior management for HTML input elements.
 * It automatically applies appropriate Bootstrap classes based on input type, size, and configuration,
 * while also handling input events and providing a clean API for form input customization.
 *
 * **Key Features:**
 * - 🎯 **Automatic Type Detection** - Detects input type and applies appropriate styling
 * - 🎪 **Flexible Sizing** - Supports different form field sizes (sm, lg, etc.)
 * - 🏷️ **Host Bindings** - Dynamic CSS class application based on state
 * - 🛡️ **Type Safety** - Type-safe size and type handling
 * - 🎨 **Bootstrap Integration** - Uses Bootstrap form control classes
 * - 🔄 **Event Handling** - Automatic input event emission
 * - 🎪 **Multiple Styles** - Plaintext, rounded, color, range input support
 *
 * **Common Use Cases:**
 * - Form input field styling
 * - Dynamic input type handling
 * - Custom input field sizes
 * - Form control appearance management
 * - Input event handling
 * - Bootstrap form integration
 *
 * @example
 * <!-- Basic input field -->
 * <input appFormInputField
 *   type="text"
 *   placeholder="Enter text">
 *
 * @example
 * <!-- Input with custom size -->
 * <input appFormInputField
 *   type="email"
 *   size="lg"
 *   placeholder="Enter email">
 *
 * @example
 * <!-- Plaintext input -->
 * <input appFormInputField
 *   type="text"
 *   [plaintext]="true"
 *   value="Read-only text">
 */
@Directive({
  selector: '[appFormInputField]', 
  exportAs: 'formInputFieldApi',
  host: {
    '[class]': '_buildClassStyleList',
  }, 
})
export class FormInputFieldDirective implements IFormInputFieldUI {

  /**
   * @method _handlerInputEvent
   * @description
   * **Handles input events** and emits the current value to parent components.
   *
   * This private method is automatically called when the input element receives
   * an input event. It extracts the current value from the event target and
   * emits it through the `onChangeEvent` EventEmitter for parent components
   * to handle.
   *
   * @param {string | number} value - The current value of the input element
   * @private
   * @example
   * // Automatically called by Angular's HostListener
   * @HostListener('input', ['$event.target.value'])
   * private _handlerInputEvent(value: string | number) {
   *   this.onChangeEvent.emit(value)
   * }
   */
  @HostListener('input', ['$event'])
  protected _handlerInputEvent(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.onChangeEvent.emit(value)
  }

  /**
   * @property onChangeEvent
   * @description
   * **Emits input value changes** to parent components for event handling.
   *
   * This EventEmitter is triggered whenever the input value changes. It provides
   * a clean way for parent components to listen to input changes without directly
   * binding to the input element's events.
   *
   * @type {EventEmitter<any>}
   * @example
   * <!-- Template usage -->
   * <input appFormInputField
   *   type="text"
   *   (onChange)="handleInputChange($event)"
   *   placeholder="Enter text">
   *
   * @example
   * <!-- Component usage -->
   * handleInputChange(value: any) {
   *   console.log('Input value changed:', value);
   *   // Handle the input change
   * }
   */
  @Output('onChange')
  public onChangeEvent: EventEmitter<any> = new EventEmitter<any>()  
  
  /**
   * @property isPlaintext
   * @description
   * **Controls plaintext styling** for the input field.
   *
   * This property applies Bootstrap's `form-control-plaintext` class when set to true,
   * which removes borders and background, making the input appear as plain text.
   * Useful for read-only or display-only input fields.
   *
   * @type {boolean}
   * @default false
   * @example
   * <!-- Plaintext input -->
   * <input appFormInputField
   *   type="text"
   *   [plaintext]="true"
   *   value="Read-only text">
   *
   * @example
   * <!-- Dynamic plaintext -->
   * <input appFormInputField
   *   type="text"
   *   [plaintext]="isReadOnly"
   *   [value]="displayValue">
   */
  @HostBinding('class.form-control-plaintext')
  @Input({ alias: 'plaintext' })
  public isPlaintext : boolean = false

  /**
   * @property size
   * @description
   * **Controls the size** of the input field using Bootstrap sizing classes.
   *
   * This property accepts `IFormFieldSizeType` and applies the appropriate Bootstrap sizing class.
   *
   * @type {IFormFieldSizeType}
   * @example
   * <!-- Small input -->
   * <input appFormInputField
   *   type="text"
   *   size="sm"
   *   placeholder="Small input">
   *
   * @example
   * <!-- Large input -->
   * <input appFormInputField
   *   type="email"
   *   size="lg"
   *   placeholder="Large input">
   *
   * @example
   * <!-- Dynamic size -->
   * <input appFormInputField
   *   type="text"
   *   [size]="currentSize"
   *   placeholder="Dynamic size">
   */
  @Input({ alias: 'size' })
  public size!: IFormFieldSizeType

  /**
   * @property isRounded
   * @description
   * **Controls rounded pill styling** for the input field.
   *
   * This property applies Bootstrap's `rounded-pill` class when set to true,
   * which gives the input field a fully rounded, pill-like appearance.
   * Useful for modern, sleek form designs.
   *
   * @type {boolean}
   * @default false
   * @example
   * <!-- Rounded input -->
   * <input appFormInputField
   *   type="text"
   *   [rounded]="true"
   *   placeholder="Rounded input">
   *
   * @example
   * <!-- Dynamic rounded styling -->
   * <input appFormInputField
   *   type="search"
   *   [rounded]="useRoundedStyle"
   *   placeholder="Search...">
   */
  @HostBinding('class.rounded-pill')
  @Input({ alias: 'rounded' })
  public isRounded : boolean = false

  /**
   * @property isInputColor
   * @description
   * **Detects if the input is a color picker** and applies appropriate styling.
   *
   * This getter automatically detects when the input type is 'color' and applies
   * Bootstrap's `form-control-color` class for proper color picker styling.
   * The detection is based on the native element's type attribute.
   *
   * @type {boolean}
   * @example
   * <!-- Color input with automatic styling -->
   * <input appFormInputField
   *   type="color"
   *   value="#ff0000">
   * <!-- Automatically gets form-control-color class -->
   */
  @HostBinding('class.form-control-color')
  public get isInputColor(): boolean {
    return this._element.nativeElement.type === InputType.Color
  }

  /**
   * @property isInputDefault
   * @description
   * **Determines if default form-control styling** should be applied.
   *
   * This getter applies Bootstrap's `form-control` class when the input is not
   * plaintext and not a range input. It provides the standard form control
   * styling for most input types.
   *
   * @type {boolean}
   * @example
   * <!-- Default form control styling -->
   * <input appFormInputField
   *   type="text"
   *   placeholder="Standard input">
   * <!-- Automatically gets form-control class -->
   */
  @HostBinding('class.form-control')
  public get isInputDefault(): boolean {
    return !this.isPlaintext && !this.isInputRange
  }

  /**
   * @property isInputRange
   * @description
   * **Detects if the input is a range slider** and applies appropriate styling.
   *
   * This getter automatically detects when the input type is 'range' and applies
   * Bootstrap's `form-range` class for proper range slider styling.
   * The detection is based on the native element's type attribute.
   *
   * @type {boolean}
   * @example
   * <!-- Range input with automatic styling -->
   * <input appFormInputField
   *   type="range"
   *   min="0"
   *   max="100"
   *   value="50">
   * <!-- Automatically gets form-range class -->
   */
  @HostBinding('class.form-range')
  public get isInputRange(): boolean {
    return this._element.nativeElement.type === InputType.Range
  }

  /**
   * @property _buildClassStyleList
   * @description
   * **Builds the dynamic class list** for the input field based on current state.
   *
   * This private getter constructs a space-separated string of CSS classes
   * based on the input field's current configuration. It filters out null/undefined
   * values and combines size classes with other dynamic classes.
   *
   * @type {string}
   * @protected
   * @example
   * // Internal usage for class generation
   * const classList = this._buildClassStyleList;
   * // Returns: "form-control form-control-lg" (for large input)
   */
  protected get _buildClassStyleList(): string {

    // init
    const _classList: Array<string | null> = [];

    if (this.size) _classList.push(this.classStyleSize);
    
    return _classList.filter(Boolean).join(' ')

  }

  /**
   * @property classStyleSize
   * @description
   * **Gets the appropriate size class** for the input field.
   *
   * This getter uses `FromFieldUtils.classStyleSize` to generate the correct
   * Bootstrap size class based on the current size setting and the control type.
   * It provides consistent sizing across different form field types.
   *
   * @type {string}
   * @example
   * // Internal usage for size class generation
   * const sizeClass = this.classStyleSize;
   * // Returns: "form-control-sm", "form-control-lg", etc.
   */
  public get classStyleSize() {
    return FromFieldUtils.classStyleSize(this.size, FormFieldUI.Control)
  }


  /**
   * @constructor
   * @description
   * **Initializes the form input field directive** with element reference.
   *
   * The constructor injects an `ElementRef<HTMLInputElement>` to access the
   * native input element for type detection and other DOM operations.
   *
   * @param {ElementRef<HTMLInputElement>} _element - Reference to the native input element
   * @example
   * // Automatically injected by Angular's dependency injection
   * constructor(private _element: ElementRef<HTMLInputElement>) {}
   */
  constructor(private _element: ElementRef<HTMLInputElement>) {}

}
